/* Copyright (c) 2020-present CloudIO Inc */

import { Page, AnyItem, ItemType } from '../../registry/ComponentRegistry';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionOnResponse';
import { getManager } from '../../registry/Manager';

const ActionOnResponseManager: ActionManagerFn<'ActionOnResponse'> = (
  platform: Platform,
): ActionManager<'ActionOnResponse'> => {
  return {
    ...baseActionManager(platform, 'ActionOnResponse'),
    defaultValue,
    isChildAllowed: (page, item, childType) =>
      getManager(childType).managerType === 'Action',
    isAllowedInsideParent: (page: Page, parent: AnyItem) => {
      return (
        [
          'ActionApproveWorkflow',
          'ActionImport',
          'ActionInvokeCloudFunction',
          'ActionRejectWorkflow',
          'ActionRunJS',
        ] as ItemType[]
      ).includes(parent.type);
    },
    props: {
      ...baseActionProps,
    },
    name: 'On Response',
    icon: { iconName: 'ellipsis' },
    description: `**Overview**<br/>
The "On Response" action is a specialized logic container designed to handle the asynchronous results of server-side operations or external scripts. It acts as a "callback" block, executing its child actions only after the parent operation (like an API call or a file import) has successfully completed and returned a response.<br/><br/>
This component cannot function independently; it is strictly an architectural element used to capture the execution flow after a long-running process finishes. It makes the data returned by that process (e.g., JSON from a server function or status counts from an import) available to its child actions, typically via the $._.response variable.

**Key Features**<br/>
* **Asynchronous Handler:** Explicitly designed to wait for the completion of parent actions that involve network requests or complex processing, ensuring that subsequent logic runs only when data is ready.
* **Strict Parent Scope:** Enforces usage validity via isAllowedInsideParent. It can only be used as a child of specific actions: ActionInvokeCloudFunction, ActionImport, ActionRunJS, ActionApproveWorkflow, and ActionRejectWorkflow.
* **Logic Grouping:** Functions as a generic container that allows any standard Action component to be added as a child (isChildAllowed), giving developers full freedom to define what to do with the response.
* **Zero Configuration:** Inherits all properties from baseActionManager and requires no additional inputs. Its context is entirely derived from the parent action.

**Properties**<br/>
* **Base Properties:** This component utilizes ...baseActionProps exclusively, accepting only the standard configuration options available to all actions.

**Use Cases**<br/>
* **API Data Processing:** Inside an Invoke Cloud Function, using "On Response" to run an ActionSet that maps the returned JSON data to a local variable or Grid component.
* **Success Feedback:** Inside an ActionImport, using "On Response" to trigger an ActionShowMessage saying "Import Successful! 500 records added" (accessed via $._.response.count).
* **Workflow Chaining:** Inside an ActionApproveWorkflow, using "On Response" to automatically navigate the user back to the main dashboard (ActionGoToPage) once the approval is registered.`,
  };
};

export default ActionOnResponseManager;
